<template>
  <div>
    <v-container class="relativeContainer">
      <v-form
        ref="form"
        v-model="internalValid"
        lazy-validation
      >
        <v-row>
          <v-col
            cols="12"
            class="mb-1"
          >
            <SmallSectionTitle
              v-if="sectionsByKey.personal_information.isVisible"
              :section-number="sectionsByKey.personal_information.number"
              :title="$t('main.enums.sections.personal_infos')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="displayField['civility']"
            cols="12"
          >
            <SwitchInput
              v-model="record.civility"
              :required="requiredField['civility']"
              :isError="formErrors.civility ? formErrors.civility.length > 0 : false"
              :rules="fieldRules['civility']"
              :label="$t('main.enums.fields.civility')"
              :options="enumsOptions.civilities"
              @input="removeFieldErrors('civility')"
            />
          </v-col>
          <v-col
            v-if="displayField['last_name']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.last_name"
              :required="requiredField['last_name']"
              :rules="fieldRules['last_name']"
              :label="$t('main.enums.fields.last_name')"
              :default="record.last_name"
              @input="removeFieldErrors('last_name')"
            />
          </v-col>
          <v-col
            v-if="displayField['first_name']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.first_name"
              :required="requiredField['first_name']"
              :rules="fieldRules['first_name']"
              :label="$t('main.enums.fields.first_name')"
              :default="record.first_name"
              @input="removeFieldErrors('first_name')"
            />
          </v-col>

          <v-col
            v-if="displayField['birth_name']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.birth_name"
              :required="requiredField['birth_name']"
              :rules="fieldRules['birth_name']"
              :label="$t('main.enums.fields.birth_name')"
              :default="record.birth_name"
              @input="removeFieldErrors('birth_name')"
            />
          </v-col>
          <v-col
            v-if="displayField['other_names']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.other_names"
              :required="requiredField['other_names']"
              :rules="fieldRules['other_names']"
              :label="$t('main.enums.fields.other_names')"
              :default="record.other_names"
              @input="removeFieldErrors('other_names')"
            />
          </v-col>
          <v-col
            v-if="displayField['date_of_birth']"
            md="6"
            cols="12"
          >
            <DatePicker
              v-model="record.date_of_birth"
              :datePickedProp="record.date_of_birth"
              :min="minDatePicked"
              :max="currentDate"
              :required="requiredField['date_of_birth']"
              :rules="fieldRules['date_of_birth']"
              :label="$t('main.enums.fields.date_of_birth')"
              labelColor="purple-dark"
              @input="removeFieldErrors('date_of_birth')"
            />
          </v-col>
          <v-col
            v-if="displayField['birth_country']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.birth_country"
              :required="requiredField['birth_country']"
              :rules="fieldRules['birth_country']"
              :label="$t('main.enums.fields.birth_country')"
              :default="record.birth_country"
              :options="enumsOptions.countries"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('birth_country')"
            />
          </v-col>
          <v-col
            v-if="displayBirthCity && displayField['birth_insee_code']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.birth_insee_code"
              :required="requiredField['birth_insee_code']"
              :rules="fieldRules['birth_insee_code']"
              :label="$t('main.enums.fields.birth_insee_code')"
              :default="record.birth_insee_code"
              :options="enumsOptions.cities"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('birth_insee_code')"
            />
          </v-col>
          <v-col
            v-if="displayField['nationalities']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.nationalities"
              :required="requiredField['nationalities']"
              :rules="fieldRules['nationalities']"
              :label="$t('main.enums.fields.nationalities')"
              multiple
              :default="record.nationalities"
              :options="enumsOptions.countries"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('nationalities')"
            />
          </v-col>
          <v-col
            v-if="displayPermitNumber && displayField['residence_permit_number']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.residence_permit_number"
              :required="requiredField['residence_permit_number']"
              :rules="fieldRules['residence_permit_number']"
              :label="$t('main.enums.fields.residence_permit_number')"
              :default="record.residence_permit_number"
              @input="removeFieldErrors('residence_permit_number')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            class="mb-1"
          >
            <SmallSectionTitle
              v-if="sectionsByKey.marital_infos.isVisible"
              :section-number="sectionsByKey.marital_infos.number"
              :title="$t('main.enums.sections.marital_infos')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="displayField['marital_status']"
            md="6"
            cols="12"
          >
            <SelectInput
              v-model="record.marital_status"
              :required="requiredField['marital_status']"
              :rules="fieldRules['marital_status']"
              :label="$t('main.enums.fields.marital_status')"
              :default="record.marital_status"
              :options="enumsOptions.maritalStatus"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('marital_status')"
            />
          </v-col>
          <v-col
            v-if="record.marital_status === 'married'"
            md="6"
            cols="12"
          >
            <SelectInput
              v-model="record.matrimonial_regime"
              :required="requiredField['matrimonial_regime']"
              :rules="fieldRules['matrimonial_regime']"
              :label="$t('main.enums.fields.matrimonial_regime')"
              :default="record.matrimonial_regime"
              :options="enumsOptions.matrimonialRegime"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('matrimonial_regime')"
            />
          </v-col>
          <v-col
            v-if="record.marital_status === 'pacs'"
            md="6"
            cols="12"
          >
            <SelectInput
              v-model="record.pacs_convention"
              :required="requiredField['pacs_convention']"
              :rules="fieldRules['pacs_convention']"
              :label="$t('main.enums.fields.pacs_convention')"
              :default="record.pacs_convention"
              :options="enumsOptions.pacsConvention"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('pacs_convention')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            class="mb-1"
          >
            <SmallSectionTitle
              v-if="sectionsByKey.professional_infos.isVisible"
              :section-number="sectionsByKey.professional_infos.number"
              :title="$t('main.enums.sections.professional_infos')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="displayField['profession']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.profession"
              :required="requiredField['profession']"
              :rules="fieldRules['profession']"
              :label="$t('main.enums.fields.profession')"
              :default="record.profession"
              :options="enumsOptions.profession"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('profession')"
            />
          </v-col>
          <v-col
            v-if="displayField['profession_sector']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.profession_sector"
              :required="requiredField['profession_sector']"
              :rules="fieldRules['profession_sector']"
              :label="$t('main.enums.fields.profession_sector')"
              :default="record.profession_sector"
              :options="enumsOptions.professionSector"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('profession_sector')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            class="mb-1"
          >
            <SmallSectionTitle
              v-if="sectionsByKey.tax_infos.isVisible"
              :section-number="sectionsByKey.tax_infos.number"
              :title="$t('main.enums.sections.tax_infos')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-if="displayField['reference_tax_number']"
            md="6"
            cols="12"
          >
            <TextInput
              v-model="record.reference_tax_number"
              :required="requiredField['reference_tax_number']"
              :rules="fieldRules['reference_tax_number']"
              :label="$t('main.enums.fields.reference_tax_number')"
              :default="record.reference_tax_number"
              @input="removeFieldErrors('reference_tax_number')"
            />
          </v-col>
          <v-col
            v-if="displayField['tax_country']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.tax_country"
              :required="requiredField['tax_country']"
              :rules="fieldRules['tax_country']"
              :hint="taxCountryHint"
              :label="$t('main.enums.fields.tax_country')"
              :default="record.tax_country"
              :options="enumsOptions.countries"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('tax_country')"
            />
          </v-col>
          <v-col
            v-if="displayField['tax_address_line_1']"
            cols="12"
          >
            <TextInput
              v-model="record.tax_address_line_1"
              :required="requiredField['tax_address_line_1']"
              :rules="fieldRules['tax_address_line_1']"
              :label="$t('main.enums.fields.tax_address_line_1')"
              icon="mdi-home-circle-outline"
              :default="record.tax_address_line_1"
              @input="removeFieldErrors('tax_address_line_1')"
            />
          </v-col>

          <v-col
            v-if="displayField['tax_address_line_2']"
            cols="12"
          >
            <TextInput
              v-model="record.tax_address_line_2"
              :required="requiredField['tax_address_line_2']"
              :rules="fieldRules['tax_address_line_2']"
              :label="$t('main.enums.fields.tax_address_line_2')"
              icon="mdi-home-circle-outline"
              :default="record.tax_address_line_2"
              @input="removeFieldErrors('tax_address_line_2')"
            />
          </v-col>

          <v-col
            v-if="displayField['tax_address_line_3']"
            cols="12"
          >
            <TextInput
              v-model="record.tax_address_line_3"
              :required="requiredField['tax_address_line_3']"
              :rules="fieldRules['tax_address_line_3']"
              :label="$t('main.enums.fields.tax_address_line_3')"
              icon="mdi-home-circle-outline"
              :default="record.tax_address_line_3"
              @input="removeFieldErrors('tax_address_line_3')"
            />
          </v-col>
          <v-col
            v-if="displayTaxCity && displayField['tax_insee_code']"
            md="6"
            cols="12"
          >
            <MultiselectInput
              v-model="record.tax_insee_code"
              :required="requiredField['tax_insee_code']"
              :rules="fieldRules['tax_insee_code']"
              :label="$t('main.enums.fields.tax_insee_code')"
              :default="record.tax_insee_code"
              :options="enumsOptions.cities"
              item-text="label"
              item-value="code"
              @input="removeFieldErrors('tax_insee_code')"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script type="text/javascript">
  import mixinUi from '@/mixins/utils/ui.js'
  import CommonMixin from '@/mixins/store/CommonMixin.js'
  import { getFieldsByProperty } from '@/utils/person.utils'

  import SmallSectionTitle from '@/components/ui/common/SmallSectionTitle'

  import TextInput from '@/components/ui/inputs/TextInput'
  import SelectInput from '@/components/ui/inputs/SelectInput'
  import SwitchInput from '@/components/ui/inputs/SwitchInput'
  import DatePicker from '@/components/ui/inputs/DatePicker'
  import MultiselectInput from '@/components/ui/inputs/MultiselectInput'
  import personTabFields from '@/components/RightDrawer/helpers/PersonTab.fields.json'
  import { getVisibleSections } from '@/components/RightDrawer/helpers/PersonTab.helper'
  import { byKey } from '@/utils/common.utils'

  export default {
    components: {
      SmallSectionTitle,
      TextInput,
      SelectInput,
      SwitchInput,
      DatePicker,
      MultiselectInput,
    },
    mixins: [mixinUi, CommonMixin,],
    props: {
      formValid: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        localFields: personTabFields.perso,
        currentDate: new Date().toISOString().substr(0, 10),
        minDatePicked: '1900-01-01',
        record: {},
        checks: {},
      }
    },
    computed: {
      sectionsByKey() {
        const sectionKeys = ['personal_information', 'marital_infos', 'professional_infos', 'tax_infos',]
        const visibleSections = getVisibleSections(
          this.requiredAttributes,
          this.optionalAttributes,
          'perso',
          sectionKeys
        )
        const sectionsByKey = byKey(visibleSections, 'key')

        return sectionsByKey
      },
      internalValid: {
        get() {
          return this.formValid
        },
        set(value) {
          return this.$emit('update:formValid', value)
        },
      },
      taxCountryHint() {
        let msg = ''
        if (
          !!this.record.tax_country &&
          this.record.tax_country !== 'FRANCE' &&
          !this.findProofOfTaxAddress()
        ) {
          msg = this.$t('main.form.tax-country-document-enquiry')
        }

        return msg
      },
      displayField() {
        const allFields = this.requiredAttributes.concat(this.optionalAttributes)

        return getFieldsByProperty(allFields, this.localFields)
      },
      requiredField() {
        return getFieldsByProperty(this.requiredAttributes, this.localFields)
      },
      fieldRules() {
        const rules = {}

        this.localFields.forEach((item) => {
          rules[item] = []
          if (this.formErrors[item]) {
            rules[item].push(this.$t(`main.enums.error.${this.formErrors[item]}`))
          }
        })

        return rules
      },
      displayPermitNumber() {
        if (!this.record.nationalities || this.record.nationalities.length === 0) return false

        return this.record.nationalities && !this.record.nationalities.find((item) => item === 'FRANCE')
      },
      displayBirthCity() {
        return this.record.birth_country === 'FRANCE'
      },
      displayTaxCity() {
        return this.record.tax_country === 'FRANCE'
      },
    },
    watch: {
      formErrors: function(val) {
        this.$refs.form.validate()
      },
    },
    mounted() {
      this.record = Object.assign({}, this.selectedProfile)
      this.checks = this.selectedProfile.person_controls[0].checks

      if (Object.keys(this.formErrors).length > 0) {
        this.$refs.form.validate()
      }
    },
    methods: {
      findProofOfTaxAddress() {
        const docs = this.record.supporting_documents
        const found = docs.find((item) => item.document_kind === 'proof_of_tax_address')

        return found && found.status === 'uploaded'
      },
      getRecord() {
        return this.record
      },
    },
  }
</script>

<style scoped lang="scss">
.description {
  font-size: 16px;
  color: $color-103_102_102_5;
}
</style>
